import { Link } from 'react-router-dom';
import { dramas } from '@/mocks/dramas';
import { weeklyCalendar, todayWeekday } from '@/mocks/calendar';
import { stats } from '@/mocks/site';

const today = weeklyCalendar.find((day) => day.weekday === todayWeekday) ?? weeklyCalendar[0];
const featured = dramas[0];

const ANCHORS = [
  { href: '#today', label: '今日更新' },
  { href: '#hot', label: '热门短剧' },
  { href: '#category', label: '主题分类' },
  { href: '#latest', label: '最新分集' },
  { href: '#calendar', label: '追剧日历' },
  { href: '#faq', label: '常见问题' },
];

export default function HeroSection() {
  return (
    <section className="relative w-full overflow-hidden border-b border-background-200">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20cinematic%20background%20of%20warm%20amber%20and%20crimson%20light%20leaks%2C%20soft%20bokeh%20circles%20and%20delicate%20film%20grain%2C%20elegant%20flowing%20gradient%20waves%2C%20soft%20vignette%20edges%2C%20artistic%20entertainment%20poster%20texture%2C%20no%20people%2C%20high%20detail&width=1600&height=900&seq=hero-baoliao-duanju&orientation=landscape"
          alt="爆料短剧首页暖色抽象背景"
          title="爆料短剧 剧集更新追踪"
          className="h-full w-full object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-background-50/95 via-background-50/75 to-background-100/40" />
      </div>

      <div className="relative mx-auto w-full max-w-content px-4 pb-8 pt-12 md:px-6 md:pb-10 md:pt-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-8">
          <div className="animate-rise lg:col-span-7">
            <p className="mb-4 inline-flex items-center gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-1.5 text-xs text-primary-700">
              <i className="ri-flashlight-fill animate-pulse-soft text-primary-500" aria-hidden="true" />
              每部剧都按连续剧集更新顺序排好
            </p>
            <h1 className="font-heading text-3xl font-black leading-tight text-foreground-950 md:text-5xl">
              爆料短剧
              <span className="mt-2 block text-xl font-bold text-primary-600 md:text-3xl">
                按连续剧集更新顺序，追剧不漏更
              </span>
            </h1>
            <p className="mt-5 max-w-xl text-sm leading-7 text-foreground-600 md:text-base">
              追剧日历标清楚每天几点更新，剧情分集介绍写明白上一集讲了什么，下一集预告提前放，全季剧情梗概一拉到底。
              不用再数集号，也不会因为乱序错过关键剧情。
            </p>

            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/dramas"
                className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-play-list-2-line" aria-hidden="true" />
                进入剧集库
              </Link>
              <Link
                to="/calendar"
                className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50/80 px-6 py-3 text-sm text-foreground-800 transition-colors hover:border-accent-400 hover:text-accent-700"
              >
                <i className="ri-calendar-2-line" aria-hidden="true" />
                查看追剧日历
              </Link>
            </div>

            <dl className="mt-10 grid grid-cols-2 gap-y-6 border-t border-background-200 pt-6 sm:grid-cols-4">
              {stats.map((item) => (
                <div key={item.label}>
                  <dt className="text-xs text-foreground-500">{item.label}</dt>
                  <dd className="font-display mt-1 text-2xl text-foreground-950 md:text-3xl">
                    {item.value}
                    <span className="ml-0.5 text-sm text-primary-600">{item.unit}</span>
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="animate-rise animate-rise-delay-2 lg:col-span-5">
            <div className="rounded-lg border border-background-200 bg-background-50/95 p-5 backdrop-blur-sm md:p-6">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-lg font-bold text-foreground-950">今日更新追踪</h2>
                <span className="rounded-md bg-accent-100 px-2 py-1 text-xs text-accent-900">
                  {today.label} · 9月10日
                </span>
              </div>

              <ul className="mt-4 space-y-3">
                {today.items.map((item) => (
                  <li key={item.dramaId}>
                    <Link
                      to={`/drama/${item.dramaId}`}
                      className="flex cursor-pointer items-center gap-3 rounded-md border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300"
                    >
                      <span className="flex h-11 w-16 flex-col items-center justify-center rounded-md bg-primary-500 text-background-50">
                        <span className="font-display text-sm leading-none">{item.time}</span>
                        <span className="mt-1 text-[10px] opacity-80">更新</span>
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm text-foreground-900">{item.dramaTitle}</span>
                        <span className="mt-0.5 block text-xs text-foreground-500">{item.range}</span>
                      </span>
                      <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden="true" />
                    </Link>
                  </li>
                ))}
              </ul>

              {featured.nextEpisode && (
                <div className="mt-4 rounded-md border border-dashed border-primary-300 bg-primary-50/70 p-4">
                  <p className="flex items-center gap-2 text-xs text-primary-700">
                    <i className="ri-notification-3-line" aria-hidden="true" />
                    下一集预告
                  </p>
                  <p className="mt-2 text-sm text-foreground-900">
                    《{featured.title}》第 {featured.nextEpisode.no} 集 · {featured.nextEpisode.title}
                  </p>
                  <p className="mt-1 line-clamp-2 text-xs leading-6 text-foreground-600">
                    {featured.nextEpisode.preview}
                  </p>
                  <Link
                    to={`/drama/${featured.id}`}
                    className="mt-3 inline-flex cursor-pointer items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
                  >
                    查看完整剧情梗概
                    <i className="ri-arrow-right-line" aria-hidden="true" />
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        <nav
          aria-label="页面锚点导航"
          className="mt-10 flex flex-wrap gap-2 border-t border-background-200 pt-5"
        >
          {ANCHORS.map((anchor) => (
            <a
              key={anchor.href}
              href={anchor.href}
              className="cursor-pointer rounded-md border border-background-300 bg-background-50/70 px-3 py-1.5 text-xs text-foreground-600 transition-colors hover:border-primary-400 hover:text-primary-600"
            >
              {anchor.label}
            </a>
          ))}
        </nav>
      </div>
    </section>
  );
}